<template>
  <div
    class="google-place-autocomplete"
    :class="{ 'is-loading': !ready }"
  >
    <div
      ref="host"
      class="google-place-autocomplete__host"
    />
  </div>
</template>

<script>
// Thin Vue 2 wrapper around Google's PlaceAutocompleteElement (the
// replacement for google.maps.places.Autocomplete, which was closed
// to new customers on 2025-03-01).
//
// Intentionally API-compatible with vue-google-autocomplete: it
// accepts the same props the existing callers pass and emits
// placechanged(data, place, id) with the same payload shape, so the
// host components (StripeTerminalAddLocation etc.) can keep their
// getAddressData handlers unchanged. Anything only needed for the
// legacy widget (e.g. the full `fields` allow-list) is accepted but
// ignored — the new element returns everything via
// Place.fetchFields and we project it back down to the legacy shape.

const ADDRESS_FIELDS = [
  'addressComponents',
  'formattedAddress',
  'location',
  'displayName',
  'id'
]

// Map PlaceAutocompleteElement's camelCase addressComponents back to
// the snake_case "data" object vue-google-autocomplete used to emit.
// Keys chosen to cover every field the downstream handlers read
// (getAddressData in StripeTerminalAddLocation / SquareLocationAdd).
const COMPONENT_TYPE_MAP = {
  street_number: 'street_number',
  route: 'route',
  subpremise: 'subpremise',
  locality: 'locality',
  postal_town: 'postal_town',
  sublocality: 'sublocality',
  administrative_area_level_1: 'administrative_area_level_1',
  administrative_area_level_2: 'administrative_area_level_2',
  country: 'country',
  postal_code: 'postal_code',
  postal_code_suffix: 'postal_code_suffix'
}

export default {
  name: 'google-place-autocomplete',
  props: {
    id: { type: String, default: '' },
    classname: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    country: { type: [String, Array], default: () => [] },
    enableGeolocation: { type: Boolean, default: false },
    // eslint-disable-next-line vue/prop-name-casing
    'enable-geolocation': { type: Boolean, default: false },
    // Accepted for backward compat with vue-google-autocomplete; the
    // new element returns a Place object and we fetch a fixed allow-
    // list. Kept so callers don't have to change their template.
    fields: { type: Array, default: () => [] }
  },
  data() {
    return {
      ready: false,
      element: null
    }
  },
  computed: {
    includedRegionCodes() {
      if (Array.isArray(this.country)) { return this.country.filter(Boolean) }
      if (typeof this.country === 'string' && this.country.length) { return [this.country] }
      return []
    },
    effectiveEnableGeolocation() {
      return this.enableGeolocation || this.$options.propsData?.['enable-geolocation']
    }
  },
  async mounted() {
    try {
      if (typeof window === 'undefined' || !window.google || !window.google.maps || typeof window.google.maps.importLibrary !== 'function') {
        console.warn('[GooglePlaceAutocomplete] google.maps.importLibrary is not available — Google Maps JS API (v=weekly) must be loaded with loading=async before this component mounts.')
        return
      }
      const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places')

      const options = {}
      if (this.includedRegionCodes.length) {
        options.includedRegionCodes = this.includedRegionCodes
      }

      const element = new PlaceAutocompleteElement(options)
      if (this.id) { element.id = this.id }
      if (this.classname) { element.classList.add(...this.classname.split(/\s+/).filter(Boolean)) }
      if (this.placeholder) { element.placeholder = this.placeholder }
      if (this.effectiveEnableGeolocation && 'geolocation' in navigator) {
        // Bias predictions toward the user's current position when
        // available — mirrors vue-google-autocomplete's :enable-
        // geolocation behaviour.
        navigator.geolocation.getCurrentPosition((pos) => {
          try {
            element.locationBias = new window.google.maps.Circle({
              center: { lat: pos.coords.latitude, lng: pos.coords.longitude },
              radius: 10000
            })
          }
          catch (_err) { /* non-fatal */ }
        })
      }

      element.addEventListener('gmp-select', this.onPlaceSelect)
      element.addEventListener('gmp-error', this.onPlaceError)

      // Force size + colour-scheme inline (highest specificity, beats
      // any shadow-DOM internal defaults that aren't styled through
      // ::part() in the current release of the element).
      element.style.setProperty('display', 'block', 'important')
      element.style.setProperty('width', '100%', 'important')
      element.style.setProperty('height', '100%', 'important')
      element.style.setProperty('color-scheme', 'light only', 'important')

      this.$refs.host.appendChild(element)
      this.element = element
      this.ready = true

      // The predictions dropdown renders position:absolute inside the
      // element's shadow root — any ancestor with overflow:hidden /
      // auto / scroll clips it (Bootstrap's .input-group, .modal-body,
      // .modal-content). CSS overrides via ::has() and manual selector
      // fallbacks proved unreliable across combinations of modal
      // wrappers. Walk up from the host and force every clipping
      // ancestor to overflow:visible. Originals are saved so they
      // restore cleanly on unmount.
      this._unclippedAncestors = []
      let ancestor = this.$el.parentElement
      while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
        const computed = window.getComputedStyle(ancestor)
        const clipsOverflow = (prop) => ['hidden', 'auto', 'scroll', 'clip'].includes(computed[prop])
        if (clipsOverflow('overflow') || clipsOverflow('overflowX') || clipsOverflow('overflowY')) {
          this._unclippedAncestors.push({
            el: ancestor,
            overflow: ancestor.style.overflow,
            overflowX: ancestor.style.overflowX,
            overflowY: ancestor.style.overflowY
          })
          ancestor.style.setProperty('overflow', 'visible', 'important')
          ancestor.style.setProperty('overflow-x', 'visible', 'important')
          ancestor.style.setProperty('overflow-y', 'visible', 'important')
        }
        ancestor = ancestor.parentElement
      }
    }
    catch (err) {
      console.error('[GooglePlaceAutocomplete] failed to initialise PlaceAutocompleteElement', err)
    }
  },
  beforeDestroy() {
    if (this.element) {
      this.element.removeEventListener('gmp-select', this.onPlaceSelect)
      this.element.removeEventListener('gmp-error', this.onPlaceError)
      this.element.remove()
      this.element = null
    }
    if (Array.isArray(this._unclippedAncestors)) {
      for (const { el, overflow, overflowX, overflowY } of this._unclippedAncestors) {
        el.style.overflow = overflow
        el.style.overflowX = overflowX
        el.style.overflowY = overflowY
      }
      this._unclippedAncestors = []
    }
  },
  methods: {
    async onPlaceSelect(event) {
      try {
        // PlacePredictionSelectEvent exposes the selection as
        // `placePrediction`; convert to a Place via toPlace() and then
        // call fetchFields() to populate addressComponents et al. The
        // older `event.place` shape was an early-beta surface that no
        // longer exists in the GA element — reading it returned
        // undefined and silently aborted the emit, so the host form's
        // selectedAddress never set and the Save button never appeared.
        const placePrediction = event.placePrediction
        if (!placePrediction) { return }
        const place = placePrediction.toPlace()
        await place.fetchFields({ fields: ADDRESS_FIELDS })

        const legacyPlace = this.toLegacyPlaceShape(place)
        const data = this.toLegacyAddressData(place)

        this.$emit('placechanged', data, legacyPlace, this.id)
      }
      catch (err) {
        console.error('[GooglePlaceAutocomplete] gmp-select handler failed', err)
        this.$emit('error', err)
      }
    },
    onPlaceError(event) {
      this.$emit('error', event)
    },
    // Project the new addressComponents array into the snake_case
    // flat-object shape vue-google-autocomplete emitted as `data`.
    // Downstream handlers (getAddressData in the host components)
    // index into this directly, so the keys must match exactly.
    toLegacyAddressData(place) {
      const data = {}
      const components = place && place.addressComponents
      if (!Array.isArray(components)) { return data }
      for (const component of components) {
        const types = component.types || []
        for (const type of types) {
          const key = COMPONENT_TYPE_MAP[type]
          if (key && !(key in data)) {
            data[key] = component.longText || component.long_name || ''
            if (type === 'administrative_area_level_1' || type === 'country') {
              data[`${key}_short`] = component.shortText || component.short_name || data[key]
            }
          }
        }
      }
      return data
    },
    // Rebuild a minimal PlaceResult-like object so downstream code that
    // reads place.address_components keeps working. The host components
    // look up country short/long names this way.
    toLegacyPlaceShape(place) {
      const legacy = {
        place_id: place.id,
        formatted_address: place.formattedAddress,
        name: place.displayName,
        address_components: []
      }
      if (Array.isArray(place.addressComponents)) {
        legacy.address_components = place.addressComponents.map(c => ({
          long_name: c.longText,
          short_name: c.shortText,
          types: c.types || []
        }))
      }
      if (place.location) {
        legacy.geometry = {
          location: {
            lat: () => place.location.lat ?? place.location.latitude,
            lng: () => place.location.lng ?? place.location.longitude
          }
        }
      }
      return legacy
    }
  }
}
</script>

<style lang="scss">
// Everything unscoped: the gmp-place-autocomplete custom element is
// appended programmatically (outside the Vue template tree) and its
// shadow DOM is pierced by ::part(). Vue 2 scoped-CSS both misses the
// element and mangles ::part() rules during rollup.

// ─── Layout / sizing ──────────────────────────────────────────────
// Bootstrap's .input-group is display: flex and expects its children
// to be .form-control / .input-group-prepend / .input-group-append /
// .btn — none of which our wrapper is. Force it to behave the same
// way so the autocomplete sits at the stretched flex height, lined up
// with the Location prepend and the Manual button.
//
// Use `flex: 1 1 0%` (Bootstrap's idiom for .form-control inside
// .input-group) rather than `flex: 1 1 auto`. `flex-basis: auto` makes
// the flex item start at its content's natural width, and because the
// host div + gmp-place-autocomplete element below both stretch to
// `width: 100%`, the basis resolves to the full container width. That
// pushes the prepend label and the Manual button onto separate lines
// (the .input-group default `flex-wrap: wrap` triggers). With
// `flex-basis: 0` the wrapper starts from zero and grows to fill the
// remaining row space, keeping prepend / autocomplete / append inline.
.input-group > .google-place-autocomplete {
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 1 1 0%;
  min-width: 0;
  margin: 0;
  margin-bottom: 0;
  // Force light colour scheme regardless of any ancestor cascade.
  color-scheme: light only;
}

.google-place-autocomplete__host {
  display: flex;
  align-items: stretch;
  width: 100%;
}

gmp-place-autocomplete,
.google-place-autocomplete gmp-place-autocomplete {
  display: block;
  width: 100%;
  align-self: stretch;
  color-scheme: light only;
  // Documented CSS custom properties on the element.
  --gmp-place-autocomplete-background-color: #fff;
  --gmp-place-autocomplete-text-color: #495057;
  --gmp-place-autocomplete-placeholder-color: #6c757d;
  --gmp-place-autocomplete-border-color: #ced4da;
  --gmp-place-autocomplete-focus-border-color: #80bdff;
  --gmp-place-autocomplete-hover-background-color: #f8f9fa;
  --gmp-place-autocomplete-selected-background-color: #e9ecef;
}

// Match Bootstrap .form-control appearance on the shadow-DOM input.
// Height is built from line-height (1.5em) + padding (0.75rem) +
// border (2px) — matches Bootstrap's default form-control height
// exactly. height: 100% lets the row inherit the flex-stretched
// height of its .input-group siblings (the Location prepend label
// and the Manual button) when those are taller.
gmp-place-autocomplete::part(input) {
  width: 100% !important;
  min-height: calc(1.5em + 0.75rem + 2px) !important;
  height: 100% !important;
  padding: 0.375rem 0.75rem !important;
  font-size: 1rem !important;
  line-height: 1.5 !important;
  color: #495057 !important;
  background-color: #fff !important;
  border: 1px solid #ced4da !important;
  border-radius: 0 !important;
  box-sizing: border-box !important;
}

// ─── Predictions list (dropdown) colour + background ──────────────
// Force-light regardless of what the element's default palette does.
// Part names vary across Places element versions, so target every
// commonly-exposed option.
gmp-place-autocomplete::part(prediction-item),
gmp-place-autocomplete::part(prediction-list),
gmp-place-autocomplete::part(prediction-container),
gmp-place-autocomplete::part(listbox),
gmp-place-autocomplete::part(dropdown),
gmp-place-autocomplete::part(popup) {
  background-color: #fff !important;
  color: #212529 !important;
  border-color: #ced4da !important;
}

gmp-place-autocomplete::part(prediction-item-main-text),
gmp-place-autocomplete::part(primary-text) {
  color: #212529 !important;
}

gmp-place-autocomplete::part(prediction-item-secondary-text),
gmp-place-autocomplete::part(secondary-text) {
  color: #6c757d !important;
}

gmp-place-autocomplete::part(prediction-item):hover,
gmp-place-autocomplete::part(prediction-item-selected) {
  background-color: #f8f9fa !important;
}

// ─── Predictions overflow (dropdown clipping) ─────────────────────
// The predictions list renders inside the element's shadow DOM,
// position:absolute relative to the input. Any ancestor with
// overflow:hidden / auto / scroll clips it. The known offenders in
// this modal stack are:
//   .input-group        — Bootstrap sets overflow:hidden for rounded
//                         corners
//   .modal-body         — overflow-y:auto so tall forms can scroll
//   .modal-dialog       — overflow:hidden on some themes
// Force them all to visible whenever the autocomplete is in-tree.
.input-group:has(.google-place-autocomplete) {
  overflow: visible;
}
.stripe-terminal-add-location .input-group,
.square-terminal-add-location .input-group,
.location-autocomplete .input-group {
  overflow: visible;
}

// :has() pierces upwards from the autocomplete to unclip every
// ancestor up to the viewport. Scoped to ancestors that contain the
// widget so the broader page scrolling isn't touched.
.modal-body:has(.google-place-autocomplete),
.modal-content:has(.google-place-autocomplete),
.modal-dialog:has(.google-place-autocomplete) {
  overflow: visible !important;
}

// Fallback for browsers without :has() — scope narrowly to the
// known modal wrappers around the Add-Location forms so we don't
// accidentally break scrolling on unrelated modals.
.stripe-terminal-add-location,
.stripe-terminal-add-location .modal-body,
.stripe-terminal-add-location .modal-content,
.square-terminal-add-location,
.square-terminal-add-location .modal-body,
.square-terminal-add-location .modal-content {
  overflow: visible !important;
}
</style>
